(() => {
  class PetCostCalculator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";

      const root = this;

      /* ---------------- currency ---------------- */
      const RATES = {
        AUD:{rate:1,     sym:"A$"},
        USD:{rate:0.66,  sym:"US$"},
        GBP:{rate:0.52,  sym:"£"},
        EUR:{rate:0.61,  sym:"€"},
        HKD:{rate:5.15,  sym:"HK$"},
        NZD:{rate:1.09,  sym:"NZ$"},
        CAD:{rate:0.90,  sym:"C$"},
        SGD:{rate:0.88,  sym:"S$"}
      };

      /* ---------------- labels ---------------- */
      const LABELS = {
        food:"Food & treats",
        routineVet:"Routine vet & vaccinations",
        prevention:"Parasite prevention",
        insurance:"Pet insurance",
        grooming:"Professional grooming",
        boarding:"Boarding / daycare / sitting",
        farrier:"Farrier",
        electricity:"Heating & UVB electricity",
        equipment:"Rugs, tack & equipment",
        power:"Electricity (heater, filter, light)",
        water:"Water, salt, testing & treatments",
        livestock:"Livestock & plant replacement",
        media:"Filter media & consumables",
        gear:"Maintenance & replacement gear",
        toys:"Toys & enrichment",
        consumables:"Bedding, litter & consumables",
        emergency:"Unexpected vet / emergency reserve"
      };
      /* which running-cost lines respond to lifestyle tier */
      const TIER_KEYS = new Set(["food","grooming","boarding","toys","consumables","equipment","media","livestock","gear"]);
      const TIER_MULT = {budget:0.75, standard:1, premium:1.6};
      /* which lines are optional add-ons (toggleable) */
      const OPTIONAL = {insurance:"Pet insurance", grooming:"Professional grooming", boarding:"Boarding / daycare / sitting"};

      /* ---------------- species data (AUD baselines, standard tier) ---------------- */
      function dog(size){
        const S = {
          small: {lifespan:15, purchase:800,  desexing:450, supplies:350, food:750,  routineVet:300, prevention:260, insurance:620,  grooming:500, boarding:700, toys:220, consumables:180, emergency:200},
          medium:{lifespan:13, purchase:900,  desexing:550, supplies:450, food:1200, routineVet:340, prevention:340, insurance:780,  grooming:720, boarding:800, toys:280, consumables:220, emergency:280},
          large: {lifespan:11, purchase:1000, desexing:650, supplies:550, food:1750, routineVet:380, prevention:430, insurance:980,  grooming:900, boarding:900, toys:340, consumables:260, emergency:380},
          giant: {lifespan:9,  purchase:1200, desexing:800, supplies:650, food:2400, routineVet:430, prevention:520, insurance:1250, grooming:1000,boarding:1000,toys:400, consumables:300, emergency:500}
        }[size];
        return {
          lifespan:S.lifespan, purchaseDefault:S.purchase,
          upfront:{desexing:S.desexing, initialVet:350, supplies:S.supplies},
          annual:{food:S.food, routineVet:S.routineVet, prevention:S.prevention, insurance:S.insurance, grooming:S.grooming, boarding:S.boarding, toys:S.toys, consumables:S.consumables, emergency:S.emergency},
          defaults:{insurance:true, grooming:false, boarding:false}
        };
      }

      function aqua(size){
        const S = {
          nano:    {horizon:6, purchase:40,  supplies:200,  initialVet:90,  food:80,  power:120, water:120, livestock:60,  media:90,  gear:60,  emergency:100},
          tropical:{horizon:8, purchase:150, supplies:700,  initialVet:180, food:180, power:350, water:220, livestock:200, media:180, gear:150, emergency:250},
          reef:    {horizon:8, purchase:800, supplies:2500, initialVet:400, food:300, power:900, water:700, livestock:500, media:350, gear:400, emergency:500}
        }[size];
        return {
          lifespan:S.horizon, purchaseDefault:S.purchase,
          upfront:{desexing:0, initialVet:S.initialVet, supplies:S.supplies},
          annual:{food:S.food, power:S.power, water:S.water, livestock:S.livestock, media:S.media, gear:S.gear, emergency:S.emergency},
          defaults:{insurance:false, grooming:false, boarding:false}
        };
      }

      const SPECIES = {
        dog:{ label:"Dog", emoji:"🐶", hasSize:true, defaultSize:"medium", sizeLabel:"Dog size",
              sizes:["small","medium","large","giant"],
              sizeLabels:{small:"Small",medium:"Medium",large:"Large",giant:"Giant"},
              build:dog,
              note:"Size is the single biggest cost driver in dogs. A giant breed can cost more than double a small dog over its life, and it usually lives fewer years to spread the spend. Desexing, insurance and food all scale with body weight, and big dogs are far more likely to need cruciate, hip and emergency surgery. Grooming is off by default, but a Poodle, Doodle or Spitz coat can add serious money every year." },

        cat:{ label:"Cat", emoji:"🐱",
              data:{ lifespan:16, purchaseDefault:300,
                upfront:{desexing:300, initialVet:300, supplies:350},
                annual:{food:700, routineVet:280, prevention:180, insurance:480, grooming:300, boarding:500, toys:150, consumables:520, emergency:220},
                defaults:{insurance:true, grooming:false, boarding:false} },
              note:"Cats look cheap until you add it up over 16+ years. Litter and consumables quietly become one of the biggest lines. Indoor cats still need parasite prevention and dental care, and blocked bladders, kidney disease and dental work are common big-ticket bills. Long-haired cats are the ones that actually need professional grooming." },

        rabbit:{ label:"Rabbit", emoji:"🐰",
              data:{ lifespan:10, purchaseDefault:80,
                upfront:{desexing:350, initialVet:200, supplies:450},
                annual:{food:700, routineVet:250, prevention:80, insurance:250, grooming:0, boarding:200, toys:150, consumables:400, emergency:250},
                defaults:{insurance:false, grooming:false, boarding:false} },
              note:"Rabbits are not a cheap starter pet. They need a rabbit-savvy vet, vaccination against calicivirus and myxomatosis in many regions, and desexing. They hide illness and crash fast with gut stasis, so an emergency reserve is not optional. Hay, greens and space cost more than people expect, and they should really be kept in bonded pairs." },

        guinea:{ label:"Guinea pig", emoji:"🐹",
              data:{ lifespan:6, purchaseDefault:60,
                upfront:{desexing:0, initialVet:100, supplies:300},
                annual:{food:500, routineVet:180, prevention:0, insurance:150, grooming:0, boarding:100, toys:80, consumables:350, emergency:180},
                defaults:{insurance:false, grooming:false, boarding:false} },
              note:"Guinea pigs need daily vitamin C, unlimited hay and a companion, because a single guinea pig is a welfare problem, so budget for at least two. Exotic-vet visits cost more than dog and cat consults, and dental and respiratory issues are common. Cheap to buy, steady to run." },

        bird:{ label:"Bird", emoji:"🦜",
              data:{ lifespan:12, purchaseDefault:150,
                upfront:{desexing:0, initialVet:120, supplies:350},
                annual:{food:250, routineVet:200, prevention:0, insurance:0, grooming:80, boarding:100, toys:200, consumables:150, emergency:200},
                defaults:{insurance:false, grooming:false, boarding:false} },
              note:"Costs shown suit small parrots like budgies and cockatiels. Birds destroy toys constantly, so enrichment is a running cost, not a one-off. They hide illness expertly and need an avian vet. Note the lifespan: larger parrots can live 30 to 60+ years, so their lifetime cost is many times this figure." },

        reptile:{ label:"Reptile", emoji:"🦎",
              data:{ lifespan:12, purchaseDefault:150,
                upfront:{desexing:0, initialVet:120, supplies:900},
                annual:{food:500, routineVet:200, prevention:0, insurance:0, grooming:0, boarding:0, toys:100, electricity:300, consumables:250, emergency:200},
                defaults:{insurance:false, grooming:false, boarding:false} },
              note:"The reptile itself is the cheap part. The vivarium, UVB, heating and thermostat are a real upfront hit, and running that heat and light shows up on your power bill every year. Most reptile illness traces back to husbandry: wrong temperature, lighting or diet, and exotic vet care is not cheap. Figures suit a bearded dragon or leopard gecko." },

        horse:{ label:"Horse", emoji:"🐴",
              data:{ lifespan:28, purchaseDefault:4000,
                upfront:{desexing:0, initialVet:600, supplies:3000},
                annual:{food:3500, boarding:4800, farrier:1400, routineVet:900, prevention:200, insurance:700, equipment:600, emergency:1200},
                defaults:{insurance:true, grooming:false, boarding:true} },
              boardingLabel:"Agistment / livery",
              note:"A horse is a different universe of cost. Agistment or livery and feed dwarf everything else, the farrier comes every six to eight weeks whether you ride or not, and dental, worming and vaccinations are ongoing. Colic and lameness can become four-figure emergencies overnight. The purchase price is often the cheapest part of owning a horse." },

        aquatics:{ label:"Fish", emoji:"🐠", hasSize:true, defaultSize:"tropical", sizeLabel:"Tank setup",
              sizes:["nano","tropical","reef"],
              sizeLabels:{nano:"Nano freshwater", tropical:"Tropical community", reef:"Saltwater reef"},
              build:aqua,
              labels:{ purchase:"Starting livestock (fish / corals)", initialVet:"Setup, cycling & water test kit", supplies:"Tank, filter, lighting & stand" },
              note:"Fish are the classic cheap pet that isn't. The livestock is pocket change; the tank, filtration, lighting and stand are the real hit, and a saltwater reef multiplies every line. Running costs are dominated by electricity and water, because heaters, filters and lights run around the clock, and reef tanks burn through salt, RO water and test kits. There is rarely a vet bill, since aquatic vets are scarce, so the emergency reserve here is really for equipment failure and livestock loss. Figures are per year of running the tank, not per fish." }
      };

      /* ---------------- state ---------------- */
      const state = { species:"dog", size:"medium", tier:"standard", currency:"AUD",
                      purchase:null, opt:{insurance:true, grooming:false, boarding:false} };

      /* ---------------- helpers ---------------- */
      function cur(){ return RATES[state.currency]; }
      function fmt(aud){ const v=Math.round(aud*cur().rate); return cur().sym + v.toLocaleString("en-US"); }

      function activeData(){
        const sp = SPECIES[state.species];
        return sp.hasSize ? sp.build(state.size) : sp.data;
      }
      function boardingLabel(){ return SPECIES[state.species].boardingLabel || LABELS.boarding; }

      /* returns {annualLines:[{key,label,amt,optional,on}], annualTotal, upfrontLines, upfrontTotal, lifespan, purchase} */
      function compute(){
        const d = activeData();
        const mult = TIER_MULT[state.tier];
        const lines = [];
        for(const key in d.annual){
          let amt = d.annual[key];
          if(amt<=0) continue;
          if(TIER_KEYS.has(key)) amt *= mult;
          const isOpt = key in OPTIONAL;
          const on = isOpt ? !!state.opt[key] : true;
          lines.push({ key, label: key==="boarding"?boardingLabel():LABELS[key], amt, optional:isOpt, on });
        }
        const annualTotal = lines.filter(l=>l.on).reduce((s,l)=>s+l.amt,0);

        const purchase = state.purchase==null ? d.purchaseDefault : Math.max(0,state.purchase);
        const L = SPECIES[state.species].labels || {};
        const up = [];
        up.push({label:L.purchase||"Purchase / adoption", amt:purchase});
        if(d.upfront.desexing>0) up.push({label:"Desexing", amt:d.upfront.desexing});
        up.push({label:L.initialVet||"Initial vet, microchip & first vaccinations", amt:d.upfront.initialVet});
        up.push({label:L.supplies||"Starter gear & equipment", amt:d.upfront.supplies * (TIER_MULT[state.tier])});
        const upfrontTotal = up.reduce((s,l)=>s+l.amt,0);

        return {lines, annualTotal, up, upfrontTotal, lifespan:d.lifespan, purchase};
      }

      /* ---------------- render ---------------- */
      function el(id) { return root.querySelector(`[id="${id}"]`); }

      function buildTabs(){
        const wrap = el("speciesTabs"); wrap.innerHTML="";
        for(const k in SPECIES){
          const b=document.createElement("button");
          b.innerHTML=`<span aria-hidden="true">${SPECIES[k].emoji}</span> ${SPECIES[k].label}`; b.dataset.k=k;
          if(k===state.species) b.className="on";
          b.onclick=()=>{ state.species=k; syncSpecies(); render(); };
          wrap.appendChild(b);
        }
      }
      function syncSpecies(){
        // tabs active
        [...el("speciesTabs").children].forEach(b=>b.className = b.dataset.k===state.species?"on":"");
        const sp=SPECIES[state.species];
        // size field
        if(sp.hasSize){
          if(!sp.sizes.includes(state.size)) state.size = sp.defaultSize || sp.sizes[0];
          el("sizeLabelText").textContent = sp.sizeLabel || "Size";
          el("sizeField").classList.remove("hide");
          const seg=el("sizeSeg"); seg.innerHTML="";
          sp.sizes.forEach(sz=>{
            const b=document.createElement("button");
            b.textContent=sp.sizeLabels[sz]; b.dataset.sz=sz;
            if(sz===state.size)b.className="on";
            b.onclick=()=>{ state.size=sz; [...seg.children].forEach(x=>x.className=x.dataset.sz===sz?"on":""); render(); };
            seg.appendChild(b);
          });
        } else {
          el("sizeField").classList.add("hide");
        }
        // reset purchase + defaults to this species
        const d = activeData();
        state.purchase = null;
        el("purchase").value = Math.round(d.purchaseDefault*cur().rate);
        state.opt = Object.assign({insurance:false,grooming:false,boarding:false}, d.defaults);
        buildToggles();
      }

      function buildToggles(){
        const wrap=el("toggles"); wrap.innerHTML="";
        const d=activeData();
        const subt={ insurance:"Recommended by most vets", grooming:"Clipping, bathing, nails", boarding:"When you travel" };
        let count=0;
        Object.keys(OPTIONAL).forEach(key=>{
          // only show a toggle if this species actually has that cost line
          if(!(key in d.annual) || d.annual[key]<=0) return;
          const row=document.createElement("div"); row.className="toggle-row";
          const label = key==="boarding"?boardingLabel():OPTIONAL[key];
          row.innerHTML=`<div class="tl">${label}<small>${subt[key]||""}</small></div>`;
          const sw=document.createElement("label"); sw.className="sw";
          const inp=document.createElement("input"); inp.type="checkbox"; inp.checked=!!state.opt[key];
          inp.onchange=()=>{ state.opt[key]=inp.checked; render(); };
          const tr=document.createElement("span"); tr.className="track";
          sw.appendChild(inp); sw.appendChild(tr); row.appendChild(sw);
          wrap.appendChild(row);
          count++;
        });
        el("addonField").classList.toggle("hide", count===0);
      }

      function render(){
        const r=compute();
        // stat tiles
        const yearly = r.annualTotal;
        const year1 = r.upfrontTotal + yearly;
        const lifetime = r.upfrontTotal + yearly * r.lifespan;
        el("stAnnual").textContent = fmt(yearly);
        el("stYear1").textContent  = fmt(year1);
        el("stLifetime").textContent = fmt(lifetime);
        el("lifespanLbl").textContent = "(~"+r.lifespan+" yrs)";
        el("monthlyNote").textContent = "about "+fmt(yearly/12)+"/month";
        el("lifeNote").textContent = "one-off setup + "+r.lifespan+" years of running costs";

        // bars (running costs, sorted desc, active ones drive the scale)
        const sorted=[...r.lines].sort((a,b)=> b.amt-a.amt);
        const maxAmt=Math.max(...sorted.map(l=>l.on?l.amt:0), 1);
        const bars=el("bars"); bars.innerHTML="";
        sorted.forEach(l=>{
          const pct = l.on ? Math.max(2,(l.amt/maxAmt)*100) : 0;
          const share = yearly>0 && l.on ? Math.round(l.amt/yearly*100) : 0;
          const d=document.createElement("div"); d.className="bar"; d.dataset.opt=l.on?"on":"off";
          d.innerHTML=`<div class="row"><span class="nm">${l.label}${l.optional&&!l.on?" <span class='hint'>(off)</span>":""}</span>
            <span class="amt">${l.on?fmt(l.amt):"&mdash;"}${l.on&&share?` <span class='hint'>${share}%</span>`:""}</span></div>
            <div class="track"><div class="fill" style="width:${pct}%"></div></div>`;
          d.title = l.on ? `${l.label}: ${fmt(l.amt)} per year (${share}% of yearly cost)` : `${l.label}: not included`;
          bars.appendChild(d);
        });
        el("annualTot").textContent = fmt(yearly)+" / yr";

        // upfront
        const up=el("upfront"); up.innerHTML="";
        r.up.forEach(l=>{
          const row=document.createElement("div"); row.className="li";
          row.innerHTML=`<span>${l.label}</span><span class="amt">${fmt(l.amt)}</span>`;
          up.appendChild(row);
        });
        el("upfrontTot").textContent = fmt(r.upfrontTotal);

        // currency bits
        el("purCur").textContent = cur().sym;
        el("ratesNote").textContent = "Converted from AUD baselines at approximate rates (1 AUD ≈ "+cur().rate+" "+state.currency+"). Not live FX.";

        // vet note
        el("vetNoteBody").innerHTML = "<p>"+SPECIES[state.species].note+"</p>";

        buildTable(r, {yearly, year1, lifetime});
      }

      function buildTable(r, t){
        const w=el("tableWrap");
        let rows = r.lines.filter(l=>l.on).sort((a,b)=>b.amt-a.amt)
            .map(l=>`<tr><td>${l.label}</td><td class="n">${fmt(l.amt)}</td></tr>`).join("");
        let uprows = r.up.map(l=>`<tr><td>${l.label}</td><td class="n">${fmt(l.amt)}</td></tr>`).join("");
        w.innerHTML = `
         <table class="data" aria-label="Full cost breakdown">
          <thead><tr><th>Yearly running cost</th><th class="n">Per year</th></tr></thead>
          <tbody>${rows}<tr class="sum"><td>Total per year</td><td class="n">${fmt(t.yearly)}</td></tr></tbody>
         </table>
         <table class="data" style="margin-top:16px">
          <thead><tr><th>One-off setup</th><th class="n">Once</th></tr></thead>
          <tbody>${uprows}<tr class="sum"><td>Total setup</td><td class="n">${fmt(r.upfrontTotal)}</td></tr></tbody>
         </table>
         <table class="data" style="margin-top:16px">
          <tbody>
            <tr><td>First-year total</td><td class="n">${fmt(t.year1)}</td></tr>
            <tr><td>Every year after</td><td class="n">${fmt(t.yearly)}</td></tr>
            <tr class="sum"><td>Lifetime (~${r.lifespan} yrs)</td><td class="n">${fmt(t.lifetime)}</td></tr>
          </tbody>
         </table>`;
      }

      /* ---------------- wire up ---------------- */
      el("tierSeg").querySelectorAll("button").forEach(b=>{
        b.onclick=()=>{ state.tier=b.dataset.tier;
          el("tierSeg").querySelectorAll("button").forEach(x=>x.classList.toggle("on",x===b)); render(); };
      });
      el("purchase").addEventListener("input", e=>{
        const raw = parseFloat(e.target.value);
        state.purchase = isNaN(raw) ? null : raw / cur().rate; // store in AUD
        render();
      });
      el("currency").addEventListener("change", e=>{
        state.currency=e.target.value;
        // keep purchase field showing converted value
        const d=activeData();
        el("purchase").value = Math.round((state.purchase==null?d.purchaseDefault:state.purchase)*cur().rate);
        render();
      });
      el("tableToggle").addEventListener("click", ()=>{
        const w=el("tableWrap"); const open=w.classList.toggle("hide")===false;
        el("tableToggle").setAttribute("aria-expanded", open?"true":"false");
        el("tableToggle").textContent = open ? "Hide full cost table" : "Show full cost table";
      });

      /* init */
      buildTabs();
      syncSpecies();
      render();
    }
  }

  if (!customElements.get("s-pet-cost-calculator")) {
    customElements.define("s-pet-cost-calculator", PetCostCalculator);
  }
})();
